"use client";

import React, { createContext, useContext, ReactNode } from "react";
import { Permission } from "@/types/roles";
import type { User } from "@/types/auth";

interface PermissionContextType {
  user: User | null;
  hasPermission: (permission: Permission) => boolean;
  hasAnyPermission: (permissions: Permission[]) => boolean;
  hasAllPermissions: (permissions: Permission[]) => boolean;
  isCompanyOwner: boolean;
  isSystemAdmin: boolean;
  userRole: User["customRole"] | null;
}

const PermissionContext = createContext<PermissionContextType | undefined>(
  undefined
);

interface PermissionProviderProps {
  children: ReactNode;
  user: User | null;
}

export function PermissionProvider({ children, user }: PermissionProviderProps) {
  const isSystemAdmin = user?.isSystemAdmin === true || user?.role === "SYSTEM_ADMIN";
  const isCompanyOwner = user?.role === "COMPANY_OWNER";
  const userRole = user?.customRole || null;

  // Prefer backend-merged permissions (getMe), fallback to customRole.permissions
  const userPermissions = new Set<string>(
    user?.permissions?.length
      ? user.permissions
      : (user?.customRole?.permissions?.map((p) => p.permission) ?? [])
  );

  const hasPermission = (permission: Permission): boolean => {
    // System admins have all permissions
    if (isSystemAdmin) return true;

    // Company owners have all permissions
    if (isCompanyOwner) return true;

    // Check if user has the specific permission
    return userPermissions.has(permission);
  };

  const hasAnyPermission = (permissions: Permission[]): boolean => {
    // System admins have all permissions
    if (isSystemAdmin) return true;

    // Company owners have all permissions
    if (isCompanyOwner) return true;

    // Check if user has any of the specified permissions
    return permissions.some((permission) => userPermissions.has(permission));
  };

  const hasAllPermissions = (permissions: Permission[]): boolean => {
    // System admins have all permissions
    if (isSystemAdmin) return true;

    // Company owners have all permissions
    if (isCompanyOwner) return true;

    // Check if user has all of the specified permissions
    return permissions.every((permission) => userPermissions.has(permission));
  };

  const value: PermissionContextType = {
    user,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isCompanyOwner,
    isSystemAdmin,
    userRole,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
}

export function usePermissions(): PermissionContextType {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error("usePermissions must be used within a PermissionProvider");
  }
  return context;
}
